import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { SEOHead } from '../../components/SEOHead';
import { BlogMeta } from '../../components/BlogMeta';
import { trackPageView } from '../../utils/analytics';

const tips = [
  {
    heading: 'Compress and resize images',
    body: 'Use modern formats such as WebP or AVIF, which offer significantly smaller file sizes than JPEG or PNG at equivalent quality. Serve images at the correct display dimensions rather than relying on CSS to scale them down. Tools like Squoosh or ImageMagick make batch compression straightforward.',
  },
  {
    heading: 'Enable browser caching',
    body: 'Set long Cache-Control headers for static assets such as JavaScript, CSS, and images so that returning visitors do not re-download unchanged files. Use immutable cache headers for versioned or fingerprinted filenames to allow indefinite caching without stale-content risk.',
  },
  {
    heading: 'Use a Content Delivery Network (CDN)',
    body: 'A CDN serves your assets from nodes geographically close to each visitor, reducing latency and improving Time to First Byte (TTFB). This is particularly effective for international audiences and has a direct positive impact on Largest Contentful Paint (LCP).',
  },
  {
    heading: 'Minify CSS and JavaScript',
    body: 'Minification removes whitespace, comments, and redundant characters from source files, reducing their transfer size. Most modern build tools — Webpack, Vite, and Rollup — apply minification automatically when building for production, so ensure your production build is properly configured.',
  },
  {
    heading: 'Defer non-critical JavaScript',
    body: 'Add the defer or async attribute to script tags for analytics, chat widgets, A/B testing libraries, and other marketing scripts. This allows the browser to parse and render the page without waiting for those scripts to download and execute, improving perceived load speed.',
  },
  {
    heading: 'Preload critical assets',
    body: 'Use <link rel="preload"> in the document head to tell the browser to fetch high-priority resources early in the loading process. Apply this to your LCP image, key web fonts, and any above-the-fold CSS that is not already in the critical path.',
  },
  {
    heading: 'Optimise web fonts',
    body: 'Set font-display: swap in your @font-face declarations so the browser renders text in a fallback font immediately, then swaps to the web font once it has loaded — avoiding invisible text. Where possible, self-host fonts to remove the extra DNS lookup and connection overhead of third-party font services.',
  },
  {
    heading: 'Reduce server response time (TTFB)',
    body: 'A slow Time to First Byte delays every subsequent step of the loading process. Improve TTFB by enabling server-side caching, optimising database queries, and considering static site generation or edge computing for pages that do not require dynamic data per request.',
  },
  {
    heading: 'Remove render-blocking resources',
    body: 'CSS and JavaScript in the document head can block the browser from rendering any content. Audit your critical rendering path and move non-essential CSS to load asynchronously. Inline the minimal CSS needed for above-the-fold content so the first paint is not delayed.',
  },
  {
    heading: 'Lazy-load below-the-fold images',
    body: 'Add the native loading="lazy" attribute to img and iframe elements not visible in the initial viewport. This defers their download until the user scrolls near them, reducing initial page weight. Do not apply lazy loading to your LCP image — it must load as early as possible.',
  },
];

export const SpeedOptimisationTips = () => {
  useEffect(() => {
    trackPageView('/blog/website-speed-optimisation-tips');
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="10 Website Speed Optimisation Tips to Improve Page Performance"
        description="Ten practical techniques to speed up your website: image compression, caching, JavaScript deferral, font loading, and more. Apply these to improve Lighthouse scores."
        canonical="https://webspeedadvisor.com/blog/website-speed-optimisation-tips"
      />

      <div className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-colors"
          >
            ← Back to Blog
          </Link>
          <h1 className="text-4xl md:text-5xl font-black font-chivo mb-6">
            10 Website Speed Optimisation Tips to Improve Page Performance
          </h1>
          <BlogMeta published="2025-01-08" readMinutes={6} dark />
          <p className="text-xl text-slate-300">
            Practical, actionable techniques you can apply today to reduce load times and improve your Lighthouse score.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16 space-y-12">

        <section>
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            Why Page Speed Matters
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700 space-y-4">
            <p>
              Page speed is one of the most consequential factors affecting your website's success. A slow-loading page
              frustrates visitors, increases bounce rates, and directly reduces conversions. Research consistently shows
              that a 1-second delay in page load time can reduce conversions by 7% or more — a significant loss for any
              business operating online.
            </p>
            <p>
              Speed also supports organic search performance. Google uses Core Web Vitals in its ranking systems, and
              pages that meet LCP, INP, and CLS thresholds give users a better experience after they click through from
              search. Improving speed therefore benefits both user experience and technical SEO work.
            </p>
            <p>
              The ten tips below cover the most impactful areas you can address, from image optimisation and caching
              through to font loading and render-blocking resources. Each applies to most websites regardless of the
              technology stack.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-8">
            10 Website Speed Optimisation Tips
          </h2>
          <ol className="space-y-8">
            {tips.map((tip, index) => (
              <li key={tip.heading} className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {index + 1}. {tip.heading}
                </h3>
                <p className="text-slate-700 leading-relaxed">{tip.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section>
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            How to Audit Your Website Speed
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700 space-y-4">
            <p>
              Knowing which of these tips will have the greatest impact on your specific site requires an audit first.
              Generic advice can point you in the right direction, but a speed test against your actual pages will
              identify the precise issues that are costing you performance points.
            </p>
            <p>
              Tools worth using include Google's PageSpeed Insights, the Lighthouse panel built into Chrome DevTools,
              and Web Speed Advisor, which provides prioritised, contextual recommendations alongside raw scores. Run
              audits on both mobile and desktop, as the two often surface different issues.
            </p>
            <p>
              Treat speed optimisation as an ongoing practice rather than a one-off task. Re-audit after deploying
              significant changes, adding third-party scripts, or switching hosting providers to catch regressions early.
            </p>
          </div>
        </section>

        <section className="bg-primary text-white rounded-xl p-8 text-center">
          <h2 className="text-3xl font-bold font-chivo mb-4">
            Find Out What's Slowing Your Site Down
          </h2>
          <p className="text-lg mb-6 text-blue-100">
            Run a free speed audit with Web Speed Advisor to see which of these issues affect your pages most.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-primary font-bold px-8 py-4 rounded-full transition-all"
          >
            <Zap size={24} />
            Run a free speed audit with Web Speed Advisor →
          </Link>
        </section>

      </div>
    </div>
  );
};

export default SpeedOptimisationTips;
